import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconArrowLeft, IconEdit, IconTrash } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useContract, useDeleteContract } from '../../queries/contracts';
import { useProjectStore } from '../../project/project-store';
import { ApiError } from '../../lib/api-error';
import { ContractFormDrawer } from './ContractFormDrawer';
import { ContractTractsPanel } from './ContractTractsPanel';
import { ContractPartiesPanel } from './ContractPartiesPanel';
import { ContractDocumentsPanel } from './ContractDocumentsPanel';
import { formatContractStatus, formatContractType } from './contract-status';
import type { Contract } from '../../types/api';

interface FieldRowProps {
  label: string;
  value?: string | number | boolean | null;
}

function FieldRow({ label, value }: FieldRowProps) {
  let display: string;
  if (value === null || value === undefined || value === '') display = '—';
  else if (typeof value === 'boolean') display = value ? 'Yes' : 'No';
  else display = String(value);
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
        {label}
      </Text>
      <Text size="sm">{display}</Text>
    </Stack>
  );
}

function OverviewPanel({ contract }: { contract: Contract }) {
  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="md">
          <Title order={5}>Lifecycle</Title>
          <SimpleGrid cols={{ base: 2, sm: 3 }}>
            <FieldRow label="Effective" value={contract.effective_date} />
            <FieldRow label="Execution" value={contract.execution_date} />
            <FieldRow
              label="Expiration"
              value={contract.expiration_date ?? 'Perpetual'}
            />
            <FieldRow label="Termination" value={contract.termination_date} />
            <FieldRow label="Renewal notice" value={contract.renewal_notice_date} />
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={5}>Recording</Title>
          <SimpleGrid cols={{ base: 2, sm: 3 }}>
            <FieldRow label="Recorded" value={contract.recorded} />
            <FieldRow label="State" value={contract.recording_state} />
            <FieldRow label="County" value={contract.recording_county} />
            <FieldRow label="Volume" value={contract.recording_volume} />
            <FieldRow label="Page" value={contract.recording_page} />
            <FieldRow
              label="Instrument #"
              value={contract.recording_instrument_number}
            />
            <FieldRow label="Recording date" value={contract.recording_date} />
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={5}>Compensation</Title>
          <SimpleGrid cols={{ base: 2, sm: 3 }}>
            <FieldRow
              label="Amount"
              value={
                contract.consideration_amount !== null
                  ? `${contract.consideration_amount} ${contract.consideration_currency ?? ''}`
                  : null
              }
            />
            <FieldRow label="Notes" value={contract.consideration_notes} />
          </SimpleGrid>
        </Stack>
      </Card>

      {contract.contract_type === 'row' && (
        <Card withBorder>
          <Stack gap="md">
            <Title order={5}>ROW dimensions</Title>
            <SimpleGrid cols={{ base: 2, sm: 3 }}>
              <FieldRow label="Use" value={contract.row_use} />
              <FieldRow label="Pipeline product" value={contract.row_pipeline_product} />
              <FieldRow label="Width (ft)" value={contract.row_width_feet} />
              <FieldRow label="Length (ft)" value={contract.row_length_feet} />
              <FieldRow label="Length (rods)" value={contract.row_length_rods} />
              <FieldRow
                label="Diameter (in)"
                value={contract.row_pipeline_diameter_inches}
              />
              <FieldRow
                label="Depth restriction (ft)"
                value={contract.row_depth_restriction_feet}
              />
              <FieldRow
                label="Surface restoration"
                value={contract.row_surface_restoration_required}
              />
            </SimpleGrid>
          </Stack>
        </Card>
      )}

      {(contract.description || contract.notes) && (
        <Card withBorder>
          <Stack gap="md">
            <Title order={5}>Description &amp; notes</Title>
            {contract.description && (
              <Stack gap={4}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Description
                </Text>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {contract.description}
                </Text>
              </Stack>
            )}
            {contract.notes && (
              <Stack gap={4}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Internal notes
                </Text>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {contract.notes}
                </Text>
              </Stack>
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = useProjectStore((s) => s.projectRole);
  const canWrite = role === 'owner' || role === 'moderator' || role === 'editor';
  const canDelete = role === 'owner' || role === 'moderator';

  const { data: contract, isLoading } = useContract(id ?? null);
  const deleteM = useDeleteContract();
  const [editOpen, setEditOpen] = useState(false);
  const [tab, setTab] = useState<string | null>('overview');

  if (!id) return null;

  if (isLoading || !contract) {
    return (
      <Stack>
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/contracts')}
          >
            Back to contracts
          </Button>
        </Group>
        <Text c="dimmed">{isLoading ? 'Loading…' : 'Contract not found'}</Text>
      </Stack>
    );
  }

  function confirmDelete() {
    if (!contract) return;
    modals.openConfirmModal({
      title: 'Soft-delete this contract?',
      children: (
        <Text size="sm">
          The contract will be hidden from listings; audit history is preserved. Detach all
          attached tracts, parties, and documents first or the API returns
          <code> contract_in_use</code>.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteM.mutateAsync(contract.id);
          notifications.show({
            title: 'Contract deleted',
            message: contract.title,
            color: 'pine',
          });
          navigate('/contracts');
        } catch (err) {
          notifications.show({
            title: 'Delete failed',
            message: err instanceof ApiError ? err.message : 'Unexpected error',
            color: 'red',
          });
        }
      },
    });
  }

  const status = formatContractStatus(contract.status);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="xs">
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconArrowLeft size={14} />}
              onClick={() => navigate('/contracts')}
            >
              All contracts
            </Button>
          </Group>
          <Title order={2}>{contract.title}</Title>
          <Group gap="sm">
            <Badge variant="light" color="indigo">
              {formatContractType(contract.contract_type)}
            </Badge>
            <Badge variant="light" color={status.color}>
              {status.label}
            </Badge>
            {contract.contract_number && (
              <Text size="sm" c="dimmed">
                #{contract.contract_number}
              </Text>
            )}
          </Group>
        </Stack>
        <Group>
          {canWrite && (
            <Tooltip label="Edit">
              <ActionIcon variant="default" size="lg" onClick={() => setEditOpen(true)}>
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip label="Delete">
              <ActionIcon variant="default" size="lg" color="red" onClick={confirmDelete}>
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>

      <Tabs value={tab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          <Tabs.Tab value="tracts">Tracts ({contract.tract_count ?? 0})</Tabs.Tab>
          <Tabs.Tab value="parties">Parties ({contract.party_count ?? 0})</Tabs.Tab>
          <Tabs.Tab value="documents">Documents ({contract.document_count ?? 0})</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <OverviewPanel contract={contract} />
        </Tabs.Panel>
        <Tabs.Panel value="tracts" pt="md">
          <ContractTractsPanel contractId={contract.id} canWrite={canWrite} />
        </Tabs.Panel>
        <Tabs.Panel value="parties" pt="md">
          <ContractPartiesPanel contractId={contract.id} canWrite={canWrite} />
        </Tabs.Panel>
        <Tabs.Panel value="documents" pt="md">
          <ContractDocumentsPanel contractId={contract.id} canWrite={canWrite} />
        </Tabs.Panel>
      </Tabs>

      <ContractFormDrawer
        opened={editOpen}
        onClose={() => setEditOpen(false)}
        contract={contract}
      />
    </Stack>
  );
}
